import { useEffect, useSyncExternalStore } from 'react'
import en from '@/translations/en.json'
import cn from '@/translations/cn.json'
import { TypeLang } from '@/store/language/language'

interface ImportTranslates {
  default: Record<string, string>
}

const fallbackMessages: Record<TypeLang, Record<string, string>> = {
  en,
  cn,
  tc: cn,
  kr: en,
  jp: en
}

const loadedMessages: Record<TypeLang, Record<string, string> | undefined> = {
  en,
  cn,
  tc: undefined,
  kr: undefined,
  jp: undefined
}

let displayedMessages: Record<string, string> = en
let requestedLang: TypeLang = 'en'

const subscribers = new Set<() => void>()

const subscribe = (cb: () => void) => {
  subscribers.add(cb)
  return () => {
    subscribers.delete(cb)
  }
}

const setLoaded = (lang: TypeLang, m: Record<string, string>) => {
  loadedMessages[lang] = m
  if (lang === requestedLang) {
    displayedMessages = m
  }
  subscribers.forEach(cb => {
    cb()
  })
}

const loadLang = (lang: TypeLang) => {
  if (loadedMessages[lang]) {
    return
  }
  let promise: Promise<ImportTranslates>
  switch (lang) {
    case 'tc':
      promise = import('@/translations/tc.json')
      break
    case 'jp':
      promise = import('@/translations/jp.json')
      break
    case 'kr':
      promise = import('@/translations/kr.json')
      break
    default:
      return
  }
  promise.then(({ default: newLang }) => {
    setLoaded(lang, newLang)
  })
}

// Look up a translated string outside of React (e.g. in middleware), using
// the loaded messages for `lang` with the same fallback as the hook.
export const getMessage = (lang: TypeLang, id: string): string => {
  const messages = loadedMessages[lang] ?? fallbackMessages[lang]
  return messages[id] ?? id
}

const useTranslates = (lang: TypeLang): Record<string, string> => {
  useEffect(() => {
    requestedLang = lang
    const loaded = loadedMessages[lang]
    if (loaded) {
      displayedMessages = loaded
    } else {
      loadLang(lang)
    }
  }, [lang])

  return useSyncExternalStore(
    subscribe,
    () => loadedMessages[lang] ?? displayedMessages
  )
}

export default useTranslates
